import React, { useState, useEffect } from "react";
import { CircularProgress, Modal } from "@mui/material";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import "./AnnouncementPostView.scss";
import ProtectedComponent from "../../../../../components/ProtectedComponent";
import NavigationLayout from "../../../navigation";
import BackArrowIcon from "../../../../../components/icons/BackArrowIcon";
import Loading from "../../../../loading/Loading";
import { clientRoutes } from "../../../../../utils/routes";
import { CommonEnums } from "../../../../../enums/common.enums";
import { CommonHelpers } from "../../../../../utils/helpers/common.helpers";
import PreviewModal from "../../propertypost/view/PreviewModal";
import { PropertyApi } from "../../../../../utils/api/Property";
import PostAssets from "./PostAssets";
import OnBoardingPostConfiguration from "./AnnouncementPostConfiguration";
import OnBoardingviewMenuIcon from "./AnnouncementviewMenuIcon";
import AnnouncementPostConfiguration from "./AnnouncementPostConfiguration";
import AnnouncementviewMenuIcon from "./AnnouncementviewMenuIcon";

interface InputField {
  handler: string;
  label: string;
  value: string;
  template_input_index: number;
  characters_limit?: number;
  input_type?: string;
  title_input?: boolean;
  use_stage_name?: boolean;
  enabled?: boolean;
}

interface AnnouncementPostData {
  _id: string;
  post_status: string;
  post_type: string;
  agency_ref: string;
  agent_ref: string;
  schedule_datetime: string | null;
  createdAt: string;
  updatedAt: string;
  inputs: Record<string, InputField>;
  design_template_id: string;
  listing_type?: string;
  image_post_design_activity_ref?: any;
  story_post_design_activity_ref?: any;
  video_post_design_activity_ref?: any;
  reel_post_design_activity_ref?: any;
}

interface FormValues {
  post_type: string;
  design_template_id: string;
  schedule_date: dayjs.Dayjs;
  schedule_time: dayjs.Dayjs;
  post_status: string;
  inputs: Record<string, InputField>;
}

const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT;

const AnnouncementPostView = () => {
  const [activeTab, setActiveTab] = useState("info");
  const [loading, setLoading] = useState(false);
  const [announcementPost, setAnnouncementPost] =
    useState<AnnouncementPostData | null>(null);
  const [onboardingViewPost, setOnBoardingViewPost] = useState<any>(null);
  const navigate = useNavigate();
  const { announcementPostId } = useParams();
  const [templateData, setTemplateData] = useState<any>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [processingModalOpen, setProcessingModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const subscriptionType =
    searchParams.get("subscription_type") || "agent_marketing";
  const postTypeFromUrl =
    searchParams.get("post_type") || announcementPost?.post_type || "";
  const listingType =
    searchParams.get("listing_type") || announcementPost?.listing_type || "sale";

  const [formInitialValues, setFormInitialValues] = useState<FormValues | null>(
    null
  );

  useEffect(() => {
    if (announcementPost && templateData) {
      setFormInitialValues({
        post_type: announcementPost.post_type || "",
        design_template_id: announcementPost.design_template_id || "",
        schedule_date: announcementPost.schedule_datetime
          ? dayjs(new Date(announcementPost.schedule_datetime))
          : dayjs(),
        schedule_time: announcementPost.schedule_datetime
          ? dayjs(new Date(announcementPost.schedule_datetime))
          : dayjs(),
        post_status: announcementPost.post_status || "",
        inputs: announcementPost.inputs || {},
      });
    }
  }, [announcementPost, templateData]);

  const onSubmitHandler = async (values: any) => {
    setProcessingModalOpen(true);

    try {
      const payload = {
        post_type: values.post_type,
        schedule_date: values.schedule_date?.format("YYYY-MM-DD"),
        schedule_time: values.schedule_time?.format("HH:mm"),
        post_status: values.post_status,
        inputs: values.inputs,
        finalize: true,
      };

      const res = await PropertyApi.updateAnnouncementPost(
        payload,
        announcementPostId as string
      );

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Internal server error!");
        setProcessingModalOpen(false);
        return;
      }

      setAnnouncementPost((prev: any) =>
        prev ? { ...prev, inputs: values.inputs } : prev
      );

      setProcessingModalOpen(false);
      setIsGenerating(true);

      const pollInterval = 3000;
      const pollDesignStatus = async () => {
        try {
          const check = await PropertyApi.GetAnnouncementPostById(
            announcementPostId as string
          );
          const onAnnouncementData = check?.data?.data;

          if (!onAnnouncementData) {
            setTimeout(pollDesignStatus, pollInterval);
            return;
          }

          const refs = [
            onAnnouncementData?.image_post_design_activity_ref,
            onAnnouncementData?.story_post_design_activity_ref,
            onAnnouncementData?.video_post_design_activity_ref,
            onAnnouncementData?.reel_post_design_activity_ref,
          ].filter(Boolean);

          const firstWithImage = refs.find(
            (ref: any) => ref?.design_task_id?.output_image_path
          );

          if (!firstWithImage) {
            setTimeout(pollDesignStatus, pollInterval);
            return;
          }

          const assetUrl = CommonHelpers.getImageUrlFromImageTool({
            path: firstWithImage.design_task_id.output_image_path,
          });

          setTemplateData((prev: any) => ({
            ...prev,
            generatedImage: assetUrl,
          }));

          setIsGenerating(false);
          toast.success("Design generated successfully!");
        } catch (err) {
          console.error("Polling error:", err);
          setTimeout(pollDesignStatus, pollInterval);
        }
      };

      pollDesignStatus();
    } catch (error) {
      toast.error("Failed to update onboarding post!");
      setProcessingModalOpen(false);
    }
  };

  const formik = useFormik<FormValues>({
    initialValues: formInitialValues || {
      post_type: "",
      design_template_id: "",
      schedule_date: dayjs(),
      schedule_time: dayjs(),
      post_status: "",
      inputs: {},
    },
    enableReinitialize: true,
    onSubmit: onSubmitHandler,
  });

  const fetchTemplate = async (signal?: AbortSignal) => {
    setIsLoading(true);
    try {
      let existingGeneratedImage: string | null = null;
      let onAnnouncementData: any = null;

      if (announcementPostId) {
        const onAnnouncementRes = await PropertyApi.GetAnnouncementPostById(
          announcementPostId
        );

        if (signal?.aborted) return;

        onAnnouncementData = onAnnouncementRes?.data?.data;
        setOnBoardingViewPost(onAnnouncementData);

        if (onAnnouncementData) {
          setAnnouncementPost(onAnnouncementData);
          setLoading(false);

          const params = new URLSearchParams(searchParams);

          if (!params.get("post_type") && onAnnouncementData.post_type) {
            params.set("post_type", onAnnouncementData.post_type);
          }

          setSearchParams(params);

          const refs = [
            onAnnouncementData?.image_post_design_activity_ref,
            onAnnouncementData?.story_post_design_activity_ref,
            onAnnouncementData?.video_post_design_activity_ref,
            onAnnouncementData?.reel_post_design_activity_ref,
          ].filter(Boolean);

          const firstWithImage = refs.find(
            (ref) => ref?.design_task_id?.output_image_path
          );

          if (firstWithImage) {
            existingGeneratedImage = CommonHelpers.getImageUrlFromImageTool({
              path: firstWithImage.design_task_id.output_image_path,
            });
          }


          const designTemplateId =
            onAnnouncementData?.image_post_design_activity_ref?.design_template_id
              ?._id || onAnnouncementData?.design_template_id;

          let templateApiData = null;

          if (designTemplateId) {
            const res = await PropertyApi.getDesignTemplateById(
              designTemplateId
            );
            if (!signal?.aborted && res.success && res.data) {
              templateApiData = res.data.data;
            }
          }

          setTemplateData({
            data: templateApiData,
            generatedImage: existingGeneratedImage,
          });

          formik.setValues((prev) => ({
            ...prev,
            design_template_id: designTemplateId || "",
            inputs: onAnnouncementData.inputs || {},
            post_type: onAnnouncementData.post_type || "",
            post_status: onAnnouncementData.post_status || "",
          }));
        }
      }
    } catch (err) {
      if (signal?.aborted) {
        console.log("Fetch aborted");
        return;
      }
      console.error(err);
      toast.error("Failed to fetch onboarding post!");
    } finally {
      if (!signal?.aborted) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    if (announcementPostId) {
      fetchTemplate(signal);
    }
    return () => {
      controller.abort();
    };
  }, [announcementPostId, subscriptionType, listingType]);

  useEffect(() => {
    const step = searchParams.get("step");

    if (step === "configure_post") {
      setActiveTab("postConfig");
    } else if (step === "post_assets") {
      setActiveTab("post_assets");
    } else {
      setActiveTab("info");
    }
  }, [searchParams]);

  const handleInputChange = (key: string, value: string) => {
    formik.setFieldValue(`inputs.${key}.value`, value);
  };

  const resetInputs = () => {
    if (announcementPost) {
      const resetInputs = { ...announcementPost.inputs };
      formik.setFieldValue("inputs", resetInputs);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const getPreviewContainerClass = (postType: string | null) => {
    switch (postType) {
      case CommonEnums.postType.story_post.key:
        return "story-post-preview";
      case CommonEnums.postType.video_post.key:
        return "video-post-preview";
      case CommonEnums.postType.reel_post.key:
        return "reel-post-preview";
      default:
        return "image-post-preview";
    }
  };

  const formatPostType = (type: string) => {
    if (!type) return "";
    return type
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const sortedInputs = formik.values.inputs
    ? Object.entries(formik.values.inputs).sort(
        ([, a], [, b]) =>
          (a?.template_input_index ?? 0) - (b?.template_input_index ?? 0)
      )
    : [];

  const autoResize = (el: HTMLTextAreaElement | null) => {
    if (el) {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    }
  };

  const getStatusBadge = (status: string | null) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "Completed";
      case "processing":
        return "Processing";
      case "pending":
        return "Pending";
      case "pending_for_social_media_upload":
        return "Pending Upload";
      case "scheduled for post":
        return "Scheduled";
      case "cancelled":
        return "Cancelled";
      case "errored":
      case "error":
        return "Error";
      default:
        return "N/A";
    }
  };

  const handleUpdateDesign = async () => {
    if (!announcementPostId) {
      toast.error("Onboarding Post ID is missing!");
      return;
    }

    const inputsPayload: any = {};
    Object.keys(formik.values.inputs || {}).forEach((key) => {
      const val = formik.values.inputs[key];
      if (val?.value) {
        inputsPayload[key] = {
          handler: val.handler || key,
          label: val.label || key,
          value: val.value,
          template_input_index: val.template_input_index ?? 0,
          characters_limit: val.characters_limit ?? 0,
        };
      }
    });

    const payload = {
      inputs: inputsPayload,
      finalize: true,
    };

    setProcessingModalOpen(true);

    try {
      const res = await PropertyApi.updateAnnouncementPost(
        payload,
        announcementPostId
      );

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Internal server error!");
        setProcessingModalOpen(false);
        return;
      }

      setProcessingModalOpen(false);
      setIsGenerating(true);

      const pollInterval = 3000;
      const pollDesignStatus = async () => {
        try {
          const check = await PropertyApi.GetAnnouncementPostById(
            announcementPostId
          );
          const onAnnouncementData = check?.data?.data;

          if (!onAnnouncementData) {
            setTimeout(pollDesignStatus, pollInterval);
            return;
          }

          const refs = [
            onAnnouncementData?.image_post_design_activity_ref,
            onAnnouncementData?.story_post_design_activity_ref,
            onAnnouncementData?.video_post_design_activity_ref,
            onAnnouncementData?.reel_post_design_activity_ref,
          ].filter(Boolean);

          const firstWithImage = refs.find(
            (ref) => ref?.design_task_id?.output_image_path
          );

          if (!firstWithImage) {
            setTimeout(pollDesignStatus, pollInterval);
            return;
          }

          const assetUrl = CommonHelpers.getImageUrlFromImageTool({
            path: firstWithImage.design_task_id.output_image_path,
          });

          setTemplateData((prev: any) => ({
            ...prev,
            generatedImage: assetUrl,
          }));

          setIsGenerating(false);
          toast.success("Design generated successfully!");
        } catch (err) {
          console.error("Polling error:", err);
          setTimeout(pollDesignStatus, pollInterval);
        }
      };

      pollDesignStatus();
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong!");
      setProcessingModalOpen(false);
    }
  };

  const getPreviewImage = () => {
    if (templateData?.generatedImage) {
      return templateData.generatedImage;
    }

    if (templateData?.data?.template_image_ref?.original_file_path) {
      return `${API_BASE_URL}/${templateData.data.template_image_ref.original_file_path}`;
    }
    if (templateData?.data?.template_image) {
      return `${API_BASE_URL}/${templateData.data.template_image}`;
    }

    return "/images/image-video-generation-error-fallback-image-for-card.png";
  };

  const previewImages: string[] = [getPreviewImage()];

  const hasInputChanges = React.useMemo(() => {
    if (
      !announcementPost?.inputs ||
      Object.keys(announcementPost.inputs).length === 0
    ) {
      return true;
    }

    if (!formik.values?.inputs) return false;

    return Object.keys(formik.values.inputs).some((key) => {
      const originalValue = announcementPost.inputs[key]?.value || "";
      const currentValue = formik.values.inputs[key]?.value || "";
      return originalValue !== currentValue;
    });
  }, [announcementPost?.inputs, formik.values.inputs]);

  return (
    <ProtectedComponent>
      <NavigationLayout>
        <div className="template-inputs-wrapper">
          <div className="top-header-bar">
            <div
              className="agent-view-back-btn"
              onClick={() => {
                const subscriptionType = searchParams.get("subscription_type");
                const backUrl = subscriptionType
                  ? `${clientRoutes.AnnouncementPostList}?subscription_type=${subscriptionType}`
                  : clientRoutes.AnnouncementPostList;

                navigate(backUrl);
              }}
            >
              <div className="back-icon">
                <BackArrowIcon />
              </div>
              <p className="bakc-text-contain">Back to List</p>
            </div>
          </div>

          <div className="header-row">
            <div className="page-title">Announcement Post</div>
            <div className="action-menu-icon-wrapper">
              {announcementPost?._id && (
                <AnnouncementviewMenuIcon
                  postId={announcementPost._id}
                  onUpdated={() => fetchTemplate()}
                  onPreview={() => setPreviewOpen(true)}
                  previewImageUrl={previewImages[0]}
                />
              )}
            </div>
          </div>

          {loading || !announcementPost ? (
            <div className="list-page-loading">
              <Loading />
            </div>
          ) : (
            <>
              <div className="auction-top-info">
                <div className="top-row">
                  <div className="info-item">
                    <label>Announcement Post Id</label>
                    <p>{announcementPost._id}</p>
                  </div>
                  <div className="info-item">
                    <label>Status</label>
                    <p
                      className={`status-badge ${announcementPost.post_status.toLowerCase()}`}
                    >
                      {getStatusBadge(announcementPost.post_status)}
                    </p>
                  </div>
                  <div className="info-item">
                    <label>Agency</label>
                    <p className="agent-name">
                      {onboardingViewPost?.agency_ref?.agency_trading_name}
                    </p>
                  </div>
                  <div className="info-item">
                    <label>Agent</label>
                    <p className="agent-name">
                      {
                        onboardingViewPost?.agent_ref
                          ?.principal_agent_first_name
                      }{" "}
                      {onboardingViewPost?.agent_ref?.principal_agent_last_name}
                    </p>
                  </div>
                </div>

                <div className="bottom-row">
                  <div className="info-item">
                    <label>Post Type</label>
                    <p>{formatPostType(announcementPost.post_type)}</p>
                  </div>
                  <div className="info-item">
                    <label>Schedule Datetime</label>
                    <p>
                      {announcementPost.schedule_datetime
                        ? formatDate(announcementPost.schedule_datetime)
                        : "Not scheduled"}
                    </p>
                  </div>
                  <div className="info-item">
                    <label>Created On</label>
                    <p>{formatDate(announcementPost.createdAt)}</p>
                  </div>
                  <div className="info-item">
                    <label>Last Updated On</label>
                    <p>{formatDate(announcementPost.updatedAt)}</p>
                  </div>
                </div>
              </div>

              <div className="custom-tabs">
                <div
                  className={`tab-item ${activeTab === "info" ? "active" : ""}`}
                  onClick={() => {
                    setActiveTab("info");
                    setSearchParams({
                      ...Object.fromEntries(searchParams),
                      step: "info",
                    });
                  }}
                >
                  Info
                </div>

                <div
                  className={`tab-item ${
                    activeTab === "post_assets" ? "active" : ""
                  }`}
                  onClick={() => {
                    setActiveTab("post_assets");
                    setSearchParams({
                      ...Object.fromEntries(searchParams),
                      step: "post_assets",
                    });
                  }}
                >
                  Post Assets
                </div>

                <div
                  className={`tab-item ${
                    activeTab === "postConfig" ? "active" : ""
                  }`}
                  onClick={() => {
                    setActiveTab("postConfig");
                    setSearchParams({
                      ...Object.fromEntries(searchParams),
                      step: "configure_post",
                    });
                  }}
                >
                  Post Configuration
                </div>
              </div>

              {activeTab === "info" && (
                <>
                  {isLoading ? (
                    <div className="list-page-loading">
                      <Loading />
                    </div>
                  ) : (
                    <form onSubmit={formik.handleSubmit}>
                      <div className="template-inputs-grid">
                        <div className="template-input-fields">
                          <div className="section-heading">Inputs</div>
                          <div className="input-description">
                            Feel free to update the inputs to customize the
                            onboarding post
                          </div>

                          {sortedInputs.map(([key, inputField]) => (
                            <div key={key} className="input-field-block">
                              <label className="input-label">
                                {inputField.label}* (
                                {formik.values.inputs[key]?.value?.length || 0}/
                                {inputField.characters_limit || 100})
                              </label>
                              <textarea
                                className="input-textarea"
                                placeholder={`${inputField.label}...`}
                                maxLength={inputField.characters_limit || 100}
                                name={`inputs.${key}.value`}
                                value={formik.values.inputs[key]?.value || ""}
                                ref={(el) => autoResize(el)}
                                onInput={(e) => {
                                  e.currentTarget.style.height = "auto";
                                  e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
                                }}
                                onChange={(e) =>
                                  handleInputChange(key, e.target.value)
                                }
                              />
                            </div>
                          ))}

                          <div className="action-text-buttons">
                            <span className="text-button" onClick={resetInputs}>
                              Reset Inputs
                            </span>
                            <span
                              className="text-button"
                              onClick={handleUpdateDesign}
                            >
                              Update & Generate New Design
                            </span>
                          </div>
                        </div>

                        <div className="template-preview">
                          <div className="section-heading">Post Preview</div>
                          <div className="template-preview-label">
                            <span>Preview of design template</span>
                            <div className="preview-actions">
                              <span
                                className="preview-link"
                                onClick={() => setPreviewOpen(true)}
                              >
                                Preview
                              </span>
                            </div>
                          </div>
                          <div
                            className={`activity-image-section ${getPreviewContainerClass(
                              postTypeFromUrl
                            )} ${isGenerating ? "loading" : ""}`}
                          >
                            {isGenerating ? (
                              <div className="post-preview-loading">
                                <div className="spinner" />
                                <h4>Your request is being processed.</h4>
                                <p>
                                  We're working on your request! Creating a
                                  high-quality output can take a little while.
                                  We appreciate your patience.
                                </p>
                                <p className="message">
                                  Feel free to wait here, or browse other pages.
                                  We'll send you a notification when your
                                  request is completed.
                                </p>
                              </div>
                            ) : (
                              <img
                                src={getPreviewImage()}
                                alt="Template Preview"
                                className="activity-thumbnail"
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src =
                                    "/images/image-video-generation-error-fallback-image-for-card.png";
                                }}
                              />
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="footer-divider" />
                      <div className="footer-buttons">
                        <button
                          className="btn btn-secondary-changes"
                          type="button"
                          onClick={() => navigate(-1)}
                        >
                          Discard Changes
                        </button>
                        <button
                          className="btn btn-primary"
                          type="submit"
                          disabled={isGenerating}
                        >
                          {isGenerating ? (
                            <CircularProgress
                              size={24}
                              style={{ color: "white" }}
                            />
                          ) : hasInputChanges ? (
                            "Update Design"
                          ) : (
                            "Save"
                          )}
                        </button>
                      </div>
                    </form>
                  )}
                </>
              )}
            </>
          )}

          <Modal
            open={processingModalOpen}
            onClose={(event, reason) => {
              if (reason !== "backdropClick") {
                setProcessingModalOpen(false);
              }
            }}
            aria-labelledby="processing-modal"
            className="add-property-modal styled-modal"
            disableEscapeKeyDown
          >
            <div className="modal-center-wrapper">
              <div className="loading-content-wrapper">
                <div className="loading-container">
                  <div className="spinner" />
                  <p className="loading-text">
                    Processing your request
                    <span className="desktop-break">
                      <br />
                    </span>
                    Please wait...
                  </p>
                </div>
              </div>
            </div>
          </Modal>

          <PreviewModal
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
            images={previewImages}
          />

          {activeTab === "post_assets" && <PostAssets />}

          {activeTab === "postConfig" && <AnnouncementPostConfiguration />}
        </div>
      </NavigationLayout>
    </ProtectedComponent>
  );
};

export default AnnouncementPostView;
